import { useCallback, useContext } from 'react';
import { UserContext } from '../../../UserContext';
import type { FeatureName, SecurityAction, SecurityDecision, SecurityDecisionBatch, SecurityTarget } from '../../types';
import { EntitySecurityContext } from '../contexts/EntitySecurityContext';
import { FeatureSecurityContext } from '../contexts/FeatureSecurityContext';
import { evaluateSecurityDecisions, type SecurityEvaluationContext } from './securityEvaluationUtils';

type EntitySecurityTarget = Pick<SecurityTarget, 'entityName' | 'entitySecurity' | 'featureName'>;

export type EntitySecurityEvaluator = {
    (action: SecurityAction, entityTarget: EntitySecurityTarget): SecurityDecision;
    (actions: SecurityAction[], entityTarget: EntitySecurityTarget): SecurityDecisionBatch;
};

/**
 * Lazy/imperative security evaluator for per-call entity targets.
 *
 * Unlike `useSecurity` (eager, one shared target), this returns a callback so each
 * invocation can pass its own entity target — useful for lists/grids and multi-action
 * toolbars. Entity security resolves from the target argument, falling back to
 * `EntitySecurityContext`. Supports single-action and batched overloads.
 */
export function useEntitySecurityEvaluator(featureName?: FeatureName): EntitySecurityEvaluator {
    const { userProfile, application, activeOrganizationAccount } = useContext(UserContext);
    const featureContextValue = useContext(FeatureSecurityContext);
    const entityContextValue = useContext(EntitySecurityContext);

    const evaluator = useCallback(
        (actionOrActions: SecurityAction | SecurityAction[], entityTarget: EntitySecurityTarget) => {
            const featureNames = featureContextValue?.featureNames ?? [];
            const entitySecurityContexts = entityContextValue?.entitySecurityContexts ?? [];
            const evaluationContext: SecurityEvaluationContext = {
                userProfile,
                application,
                activeOrganizationAccount,
                featureNames,
                entitySecurityContexts
            };
            const resolvedEntityTarget = {
                ...entityTarget,
                featureName: entityTarget.featureName ?? featureName
            };

            return evaluateSecurityDecisions(actionOrActions, resolvedEntityTarget, evaluationContext);
        },
        [
            activeOrganizationAccount,
            application,
            entityContextValue?.entitySecurityContexts,
            featureContextValue?.featureNames,
            featureName,
            userProfile
        ]
    ) as EntitySecurityEvaluator;

    return evaluator;
}
